import type { MouseEventHandler, ReactNode } from 'react';
import { Icon } from '../../atoms/Icon/Icon';
import styles from './PaginationButton.module.css';

/**
 * Figma: `_Pagination button group base` (335:21266) — one cell of the
 * pagination button group. Variants: `Icon` (False | Leading | Trailing) ×
 * `State` (Default | Active/hover | Disabled). 26px tall, HUG in both axes.
 *
 * `Icon` is really *role*, not decoration: Leading is the Previous button,
 * Trailing is Next, and False is a page number. It decides the icon, the
 * padding and which corners round, so it stays an enum rather than becoming an
 * icon slot.
 *
 * Figma's `State=Active/hover` is one variant doing two jobs — the page you are
 * on, and the pointer being over a page you could go to. `current` is the
 * former (real state, and it drives `aria-current`); hover is CSS. They share a
 * rule because the design gives them the same appearance.
 *
 * The `_` prefix marks it internal in Figma, so it is not part of the public
 * package surface — compose it through `Pagination`. It is exported here only
 * so the states story can render all nine variants side by side.
 */
export type PaginationButtonState = 'default' | 'hover';

/** Figma `Icon`. `leading` = Previous, `trailing` = Next, `none` = page number. */
export type PaginationButtonIcon = 'none' | 'leading' | 'trailing';

export type PaginationButtonProps = {
  children: ReactNode;
  icon?: PaginationButtonIcon;
  /** Figma `State=Active/hover` held on — the page you are already on. */
  current?: boolean;
  /** Figma `State=Disabled`. */
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  'aria-label'?: string;
  /**
   * Normally leave this alone: hover is CSS and `current` is positional.
   * Forcing a state is for documentation and visual regression only.
   */
  state?: PaginationButtonState;
  className?: string;
};

const iconClass: Record<PaginationButtonIcon, string> = {
  none: styles.page,
  leading: styles.leading,
  trailing: styles.trailing,
};

export function PaginationButton({
  children,
  icon = 'none',
  current = false,
  disabled = false,
  onClick,
  'aria-label': ariaLabel,
  state,
  className,
}: PaginationButtonProps) {
  const classes = [
    styles.button,
    iconClass[icon],
    current ? styles.current : null,
    /*
     * Hover is live only when nothing is forced and the button can be pressed,
     * so `:hover` reacts in the app and stays inert in a states story. Figma has
     * no hover variant for a disabled button, so a forced one is ignored.
     */
    !disabled && state === 'hover' ? styles.hover : null,
    !disabled && state === undefined ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      className={classes}
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      /*
       * Not in Figma — Figma knows the current page as a fill colour only. This
       * is what makes it legible to a screen reader.
       */
      aria-current={current ? 'page' : undefined}
    >
      {/*
       * Figma names this `left arrow` but the geometry is a mirrored chevron;
       * the repo keeps Figma's name. Decorative — the adjacent label already
       * says "Previous".
       */}
      {icon === 'leading' && (
        <span className={styles.icon}>
          <Icon name="leftArrow" />
        </span>
      )}
      {/*
       * Reef read `styles.label` here, but the stylesheet never defined `.label`,
       * so the span always rendered unclassed. Kept unclassed for parity;
       * flagged to design (docs/design/open-questions.md) in case a label style
       * was intended. Caught by check:css-modules.
       */}
      <span>{children}</span>
      {icon === 'trailing' && (
        <span className={styles.icon}>
          <Icon name="chevronRight" />
        </span>
      )}
    </button>
  );
}
